import { useMemo } from 'react'

import {
  getCenteredThanksPopupTopOffset,
  getThanksPartnerId,
  THANKS_CATEGORIES,
  THANKS_DISPLAY_MODE_BY_PLACEMENT,
  THANKS_KEYWORDS,
  THANKS_SLOT_BY_PLACEMENT,
} from 'app/Integrations/components/Thanks/config'
import type { ThanksPlacement } from 'app/Integrations/components/Thanks/config'
import type { ThanksConfiguration } from 'app/Integrations/components/Thanks/types'

interface BuildThanksConfigParams {
  placement: ThanksPlacement
  embeddedInto: HTMLElement | null
  onDisplay?: () => void
  onClose?: () => void
}

export const buildThanksConfig = ({
  placement,
  embeddedInto,
  onDisplay,
  onClose,
}: BuildThanksConfigParams): ThanksConfiguration | null => {
  const partnerId = getThanksPartnerId(placement)
  if (!partnerId) return null

  const isEmbeddedMode =
    THANKS_DISPLAY_MODE_BY_PLACEMENT[placement] === 'embedded'

  if (isEmbeddedMode && !embeddedInto) return null

  const sharedConfig = {
    partnerId,
    keywords: THANKS_KEYWORDS[placement],
    categories: THANKS_CATEGORIES,
    ...(onDisplay ? { onDisplay } : {}),
    ...(onClose ? { onClose } : {}),
  }

  if (isEmbeddedMode) {
    return {
      ...sharedConfig,
      slot: THANKS_SLOT_BY_PLACEMENT[placement],
      embeddedInto: embeddedInto as HTMLElement,
    }
  }

  return {
    ...sharedConfig,
    offsetTop: getCenteredThanksPopupTopOffset,
  }
}

export const useThanksConfig = (
  params: BuildThanksConfigParams,
): ThanksConfiguration | null => {
  const { placement, embeddedInto, onDisplay, onClose } = params

  return useMemo(() => {
    return buildThanksConfig({
      placement,
      embeddedInto,
      ...(onDisplay ? { onDisplay } : {}),
      ...(onClose ? { onClose } : {}),
    })
  }, [placement, embeddedInto, onDisplay, onClose])
}
